import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, CalendarClock, Plus, Trash2, Phone, Sparkles, Clock, Search,
  ChevronLeft, ChevronRight, X, User, Tag, CreditCard, StickyNote, Save, CalendarDays,
} from "lucide-react";
import {
  format, startOfWeek, addDays, addWeeks, addMonths, isSameDay, isSameMonth,
  startOfMonth, endOfMonth, parseISO, setHours, setMinutes, differenceInCalendarDays, isToday,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DAY_START = 7;   // 7am
const DAY_END = 22;    // 10pm
const HOUR_H = 52;     // px per hour
const PX_PER_MIN = HOUR_H / 60;
const TOTAL_H = (DAY_END - DAY_START) * HOUR_H;
const HOURS = Array.from({ length: DAY_END - DAY_START }, (_, i) => DAY_START + i);

const PALETTE = [
  { bar: "bg-blue-500", chip: "bg-blue-50 border-blue-200 text-blue-800", dot: "bg-blue-500" },
  { bar: "bg-violet-500", chip: "bg-violet-50 border-violet-200 text-violet-800", dot: "bg-violet-500" },
  { bar: "bg-emerald-500", chip: "bg-emerald-50 border-emerald-200 text-emerald-800", dot: "bg-emerald-500" },
  { bar: "bg-amber-500", chip: "bg-amber-50 border-amber-200 text-amber-800", dot: "bg-amber-500" },
  { bar: "bg-rose-500", chip: "bg-rose-50 border-rose-200 text-rose-800", dot: "bg-rose-500" },
  { bar: "bg-teal-500", chip: "bg-teal-50 border-teal-200 text-teal-800", dot: "bg-teal-500" },
  { bar: "bg-indigo-500", chip: "bg-indigo-50 border-indigo-200 text-indigo-800", dot: "bg-indigo-500" },
];
const colorFor = (a) => {
  if (a.status === "cancelled") return { bar: "bg-slate-300", chip: "bg-slate-50 border-slate-200 text-slate-400", dot: "bg-slate-300" };
  const key = a.service || a.customer_name || "x";
  let h = 0; for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

const apptStart = (a) => { try { return a.start ? parseISO(a.start) : null; } catch { return null; } };
const minsFromDayStart = (d) => (d.getHours() - DAY_START) * 60 + d.getMinutes();
const humanWhen = (d) => format(d, "EEE d MMM, h:mm a");

export default function Calendar() {
  const [appts, setAppts] = useState(null);
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/appointments");
    setAppts(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!appts) return [];
    const s = q.trim().toLowerCase();
    if (!s) return appts;
    return appts.filter((a) => [a.customer_name, a.phone, a.service].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [appts, q]);

  const scheduled = useMemo(() => filtered.filter((a) => apptStart(a)), [filtered]);
  const legacy = useMemo(() => filtered.filter((a) => !apptStart(a)), [filtered]);

  const reschedule = async (appt, newStart) => {
    const dur = appt.duration || 30;
    const newEnd = new Date(newStart.getTime() + dur * 60000);
    const patch = { start: newStart.toISOString(), end: newEnd.toISOString(), when: humanWhen(newStart) };
    setAppts((prev) => prev.map((a) => (a.id === appt.id ? { ...a, ...patch } : a)));  // optimistic
    try {
      await api.patch(`/appointments/${appt.id}`, patch);
      toast.success(`Moved to ${format(newStart, "EEE d MMM, h:mm a")}`);
    } catch {
      toast.error("Couldn't reschedule");
      load();
    }
  };

  const saveEdit = async (id, body) => {
    await api.patch(`/appointments/${id}`, body);
    toast.success("Appointment updated");
    setSelected(null); load();
  };
  const cancelAppt = async (id) => {
    await api.delete(`/appointments/${id}`);
    toast.success("Appointment cancelled");
    setSelected(null); load();
  };

  const move = (dir) => {
    if (view === "day" || view === "agenda") setCursor((c) => addDays(c, dir));
    else if (view === "week") setCursor((c) => addWeeks(c, dir));
    else setCursor((c) => addMonths(c, dir));
  };

  const rangeLabel = useMemo(() => {
    if (view === "day") return format(cursor, "EEEE, d MMMM yyyy");
    if (view === "month") return format(cursor, "MMMM yyyy");
    if (view === "agenda") return "Upcoming";
    const ws = startOfWeek(cursor, { weekStartsOn: 1 });
    const we = addDays(ws, 6);
    return `${format(ws, "d MMM")} – ${format(we, "d MMM yyyy")}`;
  }, [view, cursor]);

  if (!appts) return <div className="flex items-center gap-2 text-slate-400 py-24 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading calendar…</div>;

  return (
    <div data-testid="calendar-view" className="flex flex-col h-[calc(100vh-7rem)]">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap pb-4 border-b border-slate-100">
        <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2 mr-1"><CalendarClock className="h-6 w-6 text-blue-600" /> Calendar</h1>
        <Button data-testid="today-btn" variant="outline" size="sm" onClick={() => setCursor(new Date())} className="rounded-full">Today</Button>
        <div className="flex items-center rounded-full border border-slate-200 bg-white">
          <button data-testid="prev-btn" onClick={() => move(-1)} className="p-1.5 hover:bg-slate-50 rounded-l-full transition-colors"><ChevronLeft className="h-4 w-4 text-slate-600" /></button>
          <button data-testid="next-btn" onClick={() => move(1)} className="p-1.5 hover:bg-slate-50 rounded-r-full transition-colors"><ChevronRight className="h-4 w-4 text-slate-600" /></button>
        </div>
        <span data-testid="range-label" className="text-sm font-semibold text-slate-700 min-w-[140px]">{rangeLabel}</span>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="calendar-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bookings…" className="pl-9 h-9 w-52 rounded-full" />
          </div>
          <div className="flex rounded-full border border-slate-200 overflow-hidden text-sm">
            {["day", "week", "month", "agenda"].map((v) => (
              <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
                className={`px-3 py-1.5 capitalize font-medium transition-colors ${view === v ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{v}</button>
            ))}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-booking-btn" size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1.5 rounded-full"><Plus className="h-4 w-4" /> New</Button></DialogTrigger>
            <BookingDialog onBooked={() => { setOpen(false); load(); }} defaultDate={format(cursor, "yyyy-MM-dd")} />
          </Dialog>
        </div>
      </div>

      {/* Body */}
      <div className="flex gap-5 flex-1 min-h-0 pt-4">
        {/* Sidebar */}
        <div className="hidden lg:block w-64 shrink-0 space-y-4 overflow-y-auto pr-1">
          <MiniCalendar cursor={cursor} onPick={(d) => { setCursor(d); if (view === "month" || view === "agenda") setView("day"); }} appts={scheduled} />
          <UpcomingList appts={scheduled} onPick={(a) => setSelected(a)} />
        </div>

        {/* Main view */}
        <div className="flex-1 min-w-0 rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col">
          {view === "week" && <TimeGrid days={7} cursor={cursor} appts={scheduled} onSelect={setSelected} onReschedule={reschedule} />}
          {view === "day" && <TimeGrid days={1} cursor={cursor} appts={scheduled} onSelect={setSelected} onReschedule={reschedule} />}
          {view === "month" && <MonthView cursor={cursor} appts={scheduled} onSelect={setSelected} onPickDay={(d) => { setCursor(d); setView("day"); }} />}
          {view === "agenda" && <AgendaView appts={scheduled} legacy={legacy} onSelect={setSelected} />}
        </div>
      </div>

      <AnimatePresence>
        {selected && <AppointmentPanel appt={selected} onClose={() => setSelected(null)} onSave={saveEdit} onCancel={cancelAppt} />}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Time grid (day/week) with drag-to-reschedule ---------- */
function layoutOverlaps(items) {
  // assign each appt a column among overlapping neighbours
  const sorted = [...items].sort((a, b) => a._s - b._s || a._e - b._e);
  const cols = [];
  const placed = sorted.map((it) => {
    let ci = cols.findIndex((endT) => it._s >= endT);
    if (ci === -1) { ci = cols.length; cols.push(it._e); } else cols[ci] = it._e;
    return { ...it, _col: ci };
  });
  const total = cols.length || 1;
  return placed.map((p) => ({ ...p, _total: total }));
}

function TimeGrid({ days, cursor, appts, onSelect, onReschedule }) {
  const dragRef = useRef(null);
  const start = days === 7 ? startOfWeek(cursor, { weekStartsOn: 1 }) : cursor;
  const dayList = Array.from({ length: days }, (_, i) => addDays(start, i));

  const onDrop = (day, e) => {
    e.preventDefault();
    const d = dragRef.current; if (!d) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top - (d.offsetY || 0);
    let mins = DAY_START * 60 + y / PX_PER_MIN;
    mins = Math.max(DAY_START * 60, Math.min(DAY_END * 60 - 15, Math.round(mins / 15) * 15));
    const newStart = setMinutes(setHours(day, 0), mins);
    onReschedule(d.appt, newStart);
    dragRef.current = null;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Day headers */}
      <div className="flex border-b border-slate-100 shrink-0">
        <div className="w-14 shrink-0" />
        {dayList.map((d) => (
          <div key={d.toISOString()} className="flex-1 text-center py-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{format(d, "EEE")}</div>
            <div className={`mx-auto mt-0.5 w-8 h-8 flex items-center justify-center rounded-full font-heading font-bold ${isToday(d) ? "bg-blue-600 text-white" : "text-slate-700"}`}>{format(d, "d")}</div>
          </div>
        ))}
      </div>
      {/* Scrollable grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex" style={{ height: TOTAL_H }}>
          {/* hour gutter */}
          <div className="w-14 shrink-0 relative">
            {HOURS.map((h) => (
              <div key={h} style={{ height: HOUR_H }} className="relative">
                <span className="absolute -top-2 right-2 text-[10px] text-slate-400">{format(setHours(new Date(), h), "h a")}</span>
              </div>
            ))}
          </div>
          {/* day columns */}
          {dayList.map((day) => {
            const items = appts.filter((a) => isSameDay(apptStart(a), day)).map((a) => {
              const s = apptStart(a); const dur = a.duration || 30;
              return { a, _s: minsFromDayStart(s), _e: minsFromDayStart(s) + dur, dur, s };
            });
            const laid = layoutOverlaps(items);
            return (
              <div key={day.toISOString()} data-testid={`grid-col-${format(day, "yyyy-MM-dd")}`}
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(day, e)}
                className="flex-1 relative border-l border-slate-100">
                {HOURS.map((h) => <div key={h} style={{ height: HOUR_H }} className="border-b border-slate-50" />)}
                {isToday(day) && <NowLine />}
                {laid.map(({ a, _s, dur, _col, _total, s }) => {
                  const c = colorFor(a);
                  const top = Math.max(0, _s * PX_PER_MIN);
                  const height = Math.max(20, dur * PX_PER_MIN - 2);
                  const width = `calc(${100 / _total}% - 4px)`;
                  const left = `calc(${(100 / _total) * _col}% + 2px)`;
                  return (
                    <motion.div key={a.id} data-testid={`appt-${a.id}`} layout
                      draggable onDragStart={(e) => { dragRef.current = { appt: a, offsetY: e.nativeEvent.offsetY }; }}
                      onClick={() => onSelect(a)}
                      initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                      style={{ top, height, width, left }}
                      className={`absolute rounded-lg border ${c.chip} px-2 py-1 cursor-pointer overflow-hidden shadow-sm hover:shadow-md hover:z-10 transition-shadow ${a.status === "cancelled" ? "line-through opacity-60" : ""}`}>
                      <div className="flex items-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${c.dot} shrink-0`} />
                        <span className="text-[11px] font-semibold truncate">{format(s, "h:mm")} {a.customer_name}</span>
                      </div>
                      {height > 32 && <div className="text-[10px] opacity-70 truncate">{a.service || "Appointment"}</div>}
                    </motion.div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function NowLine() {
  const now = new Date();
  if (now.getHours() < DAY_START || now.getHours() >= DAY_END) return null;
  const top = minsFromDayStart(now) * PX_PER_MIN;
  return <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top }}><div className="h-0.5 bg-rose-500" /><div className="w-2 h-2 rounded-full bg-rose-500 -mt-1 -ml-0.5" /></div>;
}

/* ---------- Month ---------- */
function MonthView({ cursor, appts, onSelect, onPickDay }) {
  const first = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
  const cells = Array.from({ length: 42 }, (_, i) => addDays(first, i));
  return (
    <div className="flex flex-col h-full">
      <div className="grid grid-cols-7 border-b border-slate-100 shrink-0">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-center py-2 text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 grid-rows-6 flex-1">
        {cells.map((d) => {
          const items = appts.filter((a) => isSameDay(apptStart(a), d)).sort((a, b) => a.start.localeCompare(b.start));
          return (
            <div key={d.toISOString()} onClick={() => onPickDay(d)}
              className={`border-b border-r border-slate-50 p-1.5 overflow-hidden cursor-pointer hover:bg-blue-50/40 transition-colors ${!isSameMonth(d, cursor) ? "bg-slate-50/50" : ""}`}>
              <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday(d) ? "bg-blue-600 text-white" : isSameMonth(d, cursor) ? "text-slate-700" : "text-slate-300"}`}>{format(d, "d")}</div>
              <div className="space-y-0.5">
                {items.slice(0, 3).map((a) => {
                  const c = colorFor(a);
                  return <div key={a.id} data-testid={`month-appt-${a.id}`} onClick={(e) => { e.stopPropagation(); onSelect(a); }} className={`flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium truncate ${c.chip} ${a.status === "cancelled" ? "line-through opacity-60" : ""}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${c.dot} shrink-0`} />{format(apptStart(a), "h:mm")} {a.customer_name}
                  </div>;
                })}
                {items.length > 3 && <div className="text-[10px] text-slate-400 pl-1">+{items.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Agenda ---------- */
function AgendaView({ appts, legacy, onSelect }) {
  const upcoming = [...appts].sort((a, b) => a.start.localeCompare(b.start));
  const groups = {};
  upcoming.forEach((a) => { const k = a.start.slice(0, 10); (groups[k] = groups[k] || []).push(a); });
  return (
    <div className="overflow-y-auto p-4 space-y-6 h-full">
      {Object.keys(groups).length === 0 && legacy.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 p-10 text-center text-slate-400">No appointments match. When your AI books a caller in, it appears here instantly.</div>
      )}
      {Object.entries(groups).map(([day, items]) => (
        <div key={day}>
          <h2 className="text-sm font-bold text-slate-700 mb-2 sticky top-0 bg-white py-1">{format(parseISO(day + "T00:00:00"), "EEEE, d MMMM")}</h2>
          <div className="space-y-2">
            {items.map((a) => {
              const c = colorFor(a);
              return (
                <motion.div key={a.id} data-testid={`agenda-appt-${a.id}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} onClick={() => onSelect(a)}
                  className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3.5 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all">
                  <div className={`w-1.5 self-stretch rounded-full ${c.bar}`} />
                  <div className="text-center shrink-0 w-14"><p className="font-heading font-bold text-slate-900 text-sm">{format(apptStart(a), "h:mm")}</p>{a.duration && <p className="text-[10px] text-slate-400">{a.duration} min</p>}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-slate-900 truncate ${a.status === "cancelled" ? "line-through text-slate-400" : ""}`}>{a.customer_name}{a.service ? <span className="text-slate-400 font-normal"> · {a.service}</span> : ""}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">{a.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {a.phone}</span>}
                      {a.source && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${a.source.startsWith("ai") ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>{a.source.startsWith("ai") ? "Booked by AI" : "Manual"}</span>}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      ))}
      {legacy.length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-slate-700 mb-2">Other requests</h2>
          <div className="space-y-2">
            {legacy.map((a) => (
              <div key={a.id} onClick={() => onSelect(a)} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3.5 cursor-pointer hover:shadow-md transition-all">
                <div className="flex-1"><p className="font-semibold text-slate-900">{a.customer_name} <span className="text-slate-400 font-normal">· {a.service}</span></p><p className="text-xs text-slate-500">{a.when}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Mini calendar ---------- */
function MiniCalendar({ cursor, onPick, appts }) {
  const [m, setM] = useState(cursor);
  useEffect(() => { setM(cursor); }, [cursor]);
  const first = startOfWeek(startOfMonth(m), { weekStartsOn: 1 });
  const cells = Array.from({ length: 42 }, (_, i) => addDays(first, i));
  const hasAppt = (d) => appts.some((a) => isSameDay(apptStart(a), d));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold text-slate-700">{format(m, "MMMM yyyy")}</span>
        <div className="flex gap-1">
          <button data-testid="mini-prev" onClick={() => setM(addMonths(m, -1))} className="p-1 rounded hover:bg-slate-100"><ChevronLeft className="h-3.5 w-3.5 text-slate-500" /></button>
          <button data-testid="mini-next" onClick={() => setM(addMonths(m, 1))} className="p-1 rounded hover:bg-slate-100"><ChevronRight className="h-3.5 w-3.5 text-slate-500" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i} className="text-[10px] text-slate-400 font-semibold py-1">{d}</div>)}
        {cells.map((d) => (
          <button key={d.toISOString()} data-testid={`mini-day-${format(d, "yyyy-MM-dd")}`} onClick={() => onPick(d)}
            className={`relative text-xs h-7 rounded-lg transition-colors ${isSameDay(d, cursor) ? "bg-blue-600 text-white font-bold" : isToday(d) ? "text-blue-600 font-bold hover:bg-blue-50" : isSameMonth(d, m) ? "text-slate-600 hover:bg-slate-100" : "text-slate-300 hover:bg-slate-50"}`}>
            {format(d, "d")}
            {hasAppt(d) && !isSameDay(d, cursor) && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function UpcomingList({ appts, onPick }) {
  const now = new Date();
  const next = [...appts].filter((a) => apptStart(a) >= now && a.status !== "cancelled").sort((a, b) => a.start.localeCompare(b.start)).slice(0, 5);
  if (next.length === 0) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Up next</p>
      <div className="space-y-1.5">
        {next.map((a) => {
          const c = colorFor(a);
          return (
            <button key={a.id} onClick={() => onPick(a)} className="w-full text-left flex items-center gap-2 rounded-lg p-1.5 hover:bg-slate-50 transition-colors">
              <span className={`w-1.5 h-8 rounded-full ${c.bar} shrink-0`} />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">{a.customer_name}</p>
                <p className="text-[10px] text-slate-400">{format(apptStart(a), "EEE d MMM · h:mm a")}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Side panel (view/edit) ---------- */
const PAY_LABEL = { paid: "Paid", deposit_paid: "Deposit paid", pending: "Payment pending", failed: "Payment failed", refunded: "Refunded" };
function AppointmentPanel({ appt, onClose, onSave, onCancel }) {
  const s = apptStart(appt);
  const [form, setForm] = useState({
    customer_name: appt.customer_name || "", phone: appt.phone || "", service: appt.service || "",
    status: appt.status || "confirmed", notes: appt.notes || "",
    date: s ? format(s, "yyyy-MM-dd") : "", time: s ? format(s, "HH:mm") : "", duration: appt.duration || 30,
  });
  const [busy, setBusy] = useState(false);
  const c = colorFor(appt);

  const save = async () => {
    setBusy(true);
    const body = { customer_name: form.customer_name, phone: form.phone, service: form.service, status: form.status, notes: form.notes };
    if (form.date && form.time) {
      const ns = setMinutes(setHours(parseISO(form.date + "T00:00:00"), Number(form.time.split(":")[0])), Number(form.time.split(":")[1]));
      body.start = ns.toISOString();
      body.end = new Date(ns.getTime() + Number(form.duration) * 60000).toISOString();
      body.when = humanWhen(ns);
      body.duration = Number(form.duration);
    }
    try { await onSave(appt.id, body); } finally { setBusy(false); }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-slate-900/20 z-40" />
      <motion.div data-testid="appt-panel" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-50 shadow-2xl flex flex-col">
        <div className={`h-1.5 ${c.bar}`} />
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-heading font-bold text-lg text-slate-900">Appointment</h3>
          <button data-testid="panel-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <Field icon={User} label="Customer"><Input data-testid="panel-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></Field>
          <Field icon={Phone} label="Phone"><Input data-testid="panel-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field icon={Tag} label="Service"><Input data-testid="panel-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} /></Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Date"><Input data-testid="panel-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Time"><Input data-testid="panel-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></Field>
            <Field label="Mins"><Input data-testid="panel-duration" type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></Field>
          </div>
          <Field label="Status">
            <select data-testid="panel-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
              {["confirmed", "pending", "pending_payment", "completed", "cancelled"].map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
            </select>
          </Field>
          {appt.payment_status && appt.payment_status !== "none" && (
            <div className="flex items-center gap-2 text-sm text-slate-600"><CreditCard className="h-4 w-4 text-slate-400" /> {PAY_LABEL[appt.payment_status] || appt.payment_status}{appt.paid_amount ? ` · £${appt.paid_amount}` : ""}</div>
          )}
          <Field icon={StickyNote} label="Notes"><Textarea data-testid="panel-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></Field>
          {appt.source && <p className="text-xs text-slate-400">{appt.source.startsWith("ai") ? "Booked by your AI receptionist" : "Booked manually"}</p>}
        </div>
        <div className="border-t border-slate-100 p-4 flex gap-2">
          <Button data-testid="panel-cancel-appt" variant="outline" onClick={() => onCancel(appt.id)} className="text-rose-600 hover:text-rose-700 gap-1.5"><Trash2 className="h-4 w-4" /> Cancel</Button>
          <Button data-testid="panel-save" onClick={save} disabled={busy} className="flex-1 bg-blue-600 hover:bg-blue-700 gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save changes</>}</Button>
        </div>
      </motion.div>
    </>
  );
}
function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">{Icon && <Icon className="h-3.5 w-3.5" />} {label}</Label>
      {children}
    </div>
  );
}

/* ---------- New booking dialog (availability-checked) ---------- */
function BookingDialog({ onBooked, defaultDate }) {
  const [date, setDate] = useState(defaultDate || format(new Date(), "yyyy-MM-dd"));
  const [duration, setDuration] = useState(30);
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ customer_name: "", phone: "", service: "" });
  const [busy, setBusy] = useState(false);

  const loadSlots = useCallback(async () => {
    setSlots(null); setSlot("");
    try { const r = await api.get("/availability", { params: { date, duration } }); setSlots(r.data.slots); }
    catch { setSlots([]); }
  }, [date, duration]);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  const submit = async () => {
    if (!form.customer_name || !slot) return toast.error("Enter a name and pick a time");
    setBusy(true);
    try { await api.post("/appointments/book", { ...form, date, time: slot, duration }); toast.success("Appointment booked"); onBooked(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not book"); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent data-testid="booking-dialog">
      <DialogHeader><DialogTitle className="font-heading">New booking</DialogTitle>
        <DialogDescription>Pick a date and an available time slot, then add the customer's details.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Date</Label><Input data-testid="booking-date" type="date" value={date} min={format(new Date(), "yyyy-MM-dd")} onChange={(e) => setDate(e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs">Duration (min)</Label><Input data-testid="booking-duration" type="number" value={duration} onChange={(e) => setDuration(Number(e.target.value) || 30)} className="mt-1" /></div>
        </div>
        <div>
          <Label className="text-xs">Available times</Label>
          <div className="mt-1 flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {slots === null && <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Checking availability…</span>}
            {slots && slots.length === 0 && <span className="text-xs text-slate-400">No free slots that day (check opening hours).</span>}
            {slots && slots.map((s) => (
              <button key={s.time} data-testid={`slot-${s.time}`} onClick={() => setSlot(s.time)} className={`rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors ${slot === s.time ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>{s.time}</button>
            ))}
          </div>
        </div>
        <div><Label className="text-xs">Customer name</Label><Input data-testid="booking-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Phone</Label><Input data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Service</Label><Input data-testid="booking-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="mt-1" /></div>
        </div>
        <Button data-testid="confirm-booking-btn" onClick={submit} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" /> Book appointment</>}</Button>
      </div>
    </DialogContent>
  );
}
